import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { EnumerationItem } from 'src/app/models/simple-enum-item.model';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import { CdContactInfo } from 'src/app/cd-request/models/closing-disclosure-doc-request.model';
import { EnumerationService } from 'src/app/services/enumeration-service';
import {
  CdContactCopySource,
  CdContactCopySourceProvider,
  CdContactSnapshot
} from 'src/app/modules/cd-request/components/wholesale-cd-wizard/steps/cd-contact-copy';
import { copyToClipboard } from 'src/utils';

export interface SettlementAgentFormData {
  companyName: string;
  companyLicenseId: string;
  contactName: string;
  contactLicense: string;
  contactPhone: string;
  contactEmail: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

@Component({
  selector: 'settlement-agent',
  templateUrl: './settlement-agent.component.html',
  styleUrls: ['./settlement-agent.component.scss'],
  standalone: false
})
export class SettlementAgentComponent extends ApplicationContextBoundComponent implements OnInit, OnChanges {

  @ViewChild('settlementAgentForm') settlementAgentForm: NgForm;

  @Input() settlementInformation: CdContactInfo | undefined;

  /** When set, a "Copy From" menu offers the contact data entered on the wizard's other steps. */
  @Input() copySourceProvider: CdContactCopySourceProvider | null = null;

  formData: SettlementAgentFormData = {
    companyName: '',
    companyLicenseId: '',
    contactName: '',
    contactLicense: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  };

  isLoading: boolean = false;
  states: EnumerationItem[] = [];
  copySources: CdContactCopySource[] = [];

  constructor(
    protected readonly injector: Injector,
    private readonly _enumService: EnumerationService
  ) {
    super(injector);
    this.states = this._enumService.states;
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settlementInformation']) {
      this.populateFormFromApiData();
    }
  }

  validateForm(): boolean {
    if (this.settlementAgentForm) {
      this.settlementAgentForm.form.markAllAsTouched();
      return this.settlementAgentForm.form.valid;
    }
    return true;
  }

  getFormData(): SettlementAgentFormData {
    return { ...this.formData };
  }

  onCopyFromToggled(isOpen: boolean): void {
    if (isOpen) {
      this.copySources = this.copySourceProvider?.getContactCopySources('settlementAgent') ?? [];
    }
  }

  copyFrom(source: CdContactCopySource): void {
    this.applySnapshot(source.snapshot);
    this.settlementAgentForm?.form.markAsDirty();
  }

  copyContactInfo(): void {
    const lines = [
      this.formData.companyName,
      this.formData.address,
      `${this.formData.city || ''}, ${this.formData.state || ''} ${this.formData.zip || ''}`,
      `Contact: ${this.formData.contactName || ''}`,
      `Email: ${this.formData.contactEmail || ''}`,
      `Phone: ${this.formData.contactPhone || ''}`
    ].filter(l => l && String(l).trim() !== '' && String(l).trim() !== ',');
    copyToClipboard(lines.join('\n'));
  }

  private applySnapshot(snapshot: CdContactSnapshot): void {
    this.formData.companyName = snapshot.companyName;
    this.formData.companyLicenseId = snapshot.companyLicenseId;
    this.formData.address = snapshot.address;
    this.formData.city = snapshot.city;
    this.formData.state = snapshot.state;
    this.formData.zip = snapshot.zip;
    this.formData.contactName = snapshot.contactName;
    this.formData.contactLicense = snapshot.contactLicense;
    this.formData.contactEmail = snapshot.contactEmail;
    this.formData.contactPhone = snapshot.contactPhone;
  }

  private populateFormFromApiData(): void {
    if (!this.settlementInformation) {
      return;
    }

    this.formData.companyName = this.settlementInformation.companyName || '';
    this.formData.companyLicenseId = this.settlementInformation.companyLicenseId || '';
    this.formData.contactName = this.settlementInformation.contactFullName || '';
    this.formData.contactLicense = this.settlementInformation.contactLicenseId || '';
    this.formData.contactPhone = this.settlementInformation.contactPhoneNumber || '';
    this.formData.contactEmail = this.settlementInformation.contactEmailAddress || '';
    this.formData.address = this.settlementInformation.companyAddress || '';
    this.formData.city = this.settlementInformation.city || '';
    this.formData.state = this.settlementInformation.state || '';
    this.formData.zip = this.settlementInformation.zip || '';
  }
}
